// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 09-data-brokering.md
// @greenlight-spec: decisions/0026-integration-catalog-and-connection-profiles.md

import * as React from 'react';
import type { CatalogAuthConfigFieldDto, CatalogConnectionProfileDto } from '@greenlight/shared';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import {
  credentialPlacementLabel,
  nonSecretAuthParamNames,
  type BasicKeyPosition,
  type CustomAuthInjectKind,
} from './catalogRegistration';

export function CredentialPlacementFields({
  isCatalog,
  isClientCreds,
  profile,
  authInjectKind,
  basicKeyPosition,
  headerName,
  queryParamName,
  username,
  nonSecretParams,
  error,
  usernameError,
  nonSecretParamErrors,
  onAuthInjectKindChange,
  onBasicKeyPositionChange,
  onHeaderNameChange,
  onQueryParamNameChange,
  onUsernameChange,
  onNonSecretParamChange,
  showPlacement = true,
  showCustomerFields = true,
}: {
  isCatalog: boolean;
  isClientCreds: boolean;
  profile: CatalogConnectionProfileDto | null;
  authInjectKind: CustomAuthInjectKind;
  basicKeyPosition: BasicKeyPosition;
  headerName: string;
  queryParamName: string;
  username: string;
  nonSecretParams: Record<string, string>;
  error?: string;
  usernameError?: string;
  nonSecretParamErrors?: Record<string, string>;
  onAuthInjectKindChange: (value: CustomAuthInjectKind) => void;
  onBasicKeyPositionChange: (value: BasicKeyPosition) => void;
  onHeaderNameChange: (value: string) => void;
  onQueryParamNameChange: (value: string) => void;
  onUsernameChange: (value: string) => void;
  onNonSecretParamChange: (name: string, value: string) => void;
  /** Catalog defaults can render the read-only placement separately from customer inputs. */
  showPlacement?: boolean;
  showCustomerFields?: boolean;
}): React.JSX.Element {
  const catalogInject = profile?.auth_inject ?? null;
  const needsUsername =
    catalogInject?.kind === 'basic'
      ? catalogInject.username_from !== 'secret'
      : !isCatalog && authInjectKind === 'basic' && basicKeyPosition === 'password';

  if (isCatalog || isClientCreds) {
    return (
      <>
        {showPlacement && (
          <div className="space-y-1.5">
            <Label htmlFor="reg-auth-placement">Credential placement</Label>
            <Input
              id="reg-auth-placement"
              value={credentialPlacementLabel(isClientCreds ? null : catalogInject, isClientCreds)}
              readOnly
              className="bg-muted/30"
            />
            <p className="text-[11.5px] text-muted-foreground">
              {isCatalog
                ? 'Defined by the catalog connection profile.'
                : 'OAuth access tokens are sent as bearer tokens.'}
            </p>
          </div>
        )}
        {showCustomerFields && needsUsername && (
          <BasicAuthUsernameField
            value={username}
            error={usernameError}
            onChange={onUsernameChange}
          />
        )}
        {showCustomerFields &&
          nonSecretAuthParamNames(profile).map((name) => (
            <div key={name} className="space-y-1.5">
              <Label htmlFor={`reg-auth-param-${name}`}>{authParamLabel(name)}</Label>
              <Input
                id={`reg-auth-param-${name}`}
                value={nonSecretParams[name] ?? ''}
                onChange={(event) => onNonSecretParamChange(name, event.target.value)}
                autoComplete="off"
              />
              {nonSecretParamErrors?.[name] && (
                <p className="text-[11.5px] text-destructive">{nonSecretParamErrors[name]}</p>
              )}
            </div>
          ))}
        {showCustomerFields && (
          <AuthConfigFieldInputs
            fields={profile?.auth_config_fields ?? []}
            values={nonSecretParams}
            errors={nonSecretParamErrors}
            onChange={onNonSecretParamChange}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div className="space-y-1.5">
        <Label htmlFor="reg-auth-placement">Credential placement</Label>
        <Select value={authInjectKind} onValueChange={onAuthInjectKindChange}>
          <SelectTrigger id="reg-auth-placement">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="bearer">Bearer token</SelectItem>
            <SelectItem value="basic">Basic authentication</SelectItem>
            <SelectItem value="custom_header">Custom header</SelectItem>
            <SelectItem value="query_param">Query parameter</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-[11.5px] text-muted-foreground">
          Choose the one wire location where Greenlight places the vaulted API key.
        </p>
      </div>

      {authInjectKind === 'basic' && (
        <div className="space-y-1.5">
          <Label htmlFor="reg-basic-key-position">API key position</Label>
          <Select value={basicKeyPosition} onValueChange={onBasicKeyPositionChange}>
            <SelectTrigger id="reg-basic-key-position">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="username">Username (empty password)</SelectItem>
              <SelectItem value="password">Password (configured username)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {needsUsername && (
        <BasicAuthUsernameField
          value={username}
          error={usernameError}
          onChange={onUsernameChange}
        />
      )}

      {authInjectKind === 'custom_header' && (
        <div className="space-y-1.5">
          <Label htmlFor="reg-auth-header-name">Header name</Label>
          <Input
            id="reg-auth-header-name"
            value={headerName}
            onChange={(event) => onHeaderNameChange(event.target.value)}
            placeholder="e.g. X-Api-Key"
            className="font-mono"
            autoComplete="off"
          />
          {error && <p className="text-[11.5px] text-destructive">{error}</p>}
        </div>
      )}

      {authInjectKind === 'query_param' && (
        <div className="space-y-1.5">
          <Label htmlFor="reg-auth-query-name">Query parameter name</Label>
          <Input
            id="reg-auth-query-name"
            value={queryParamName}
            onChange={(event) => onQueryParamNameChange(event.target.value)}
            placeholder="e.g. api_key"
            className="font-mono"
            autoComplete="off"
          />
          {error && <p className="text-[11.5px] text-destructive">{error}</p>}
        </div>
      )}
    </>
  );
}

/** A catalog profile's customer-specific auth_config inputs, at register and edit. */
export function AuthConfigFieldInputs({
  fields,
  values,
  errors,
  onChange,
}: {
  fields: readonly CatalogAuthConfigFieldDto[];
  values: Readonly<Record<string, string>>;
  errors?: Readonly<Record<string, string>>;
  onChange: (key: string, value: string) => void;
}): React.JSX.Element {
  return (
    <>
      {fields.map((field) => (
        <div key={field.key} className="space-y-1.5">
          <Label htmlFor={`reg-auth-param-${field.key}`}>{field.label}</Label>
          <Input
            id={`reg-auth-param-${field.key}`}
            value={values[field.key] ?? ''}
            onChange={(event) => onChange(field.key, event.target.value)}
            placeholder={field.example ?? undefined}
            inputMode={field.format === 'digits' ? 'numeric' : undefined}
            className="font-mono"
            autoComplete="off"
          />
          {errors?.[field.key] ? (
            <p className="text-[11.5px] text-destructive">{errors[field.key]}</p>
          ) : field.hint ? (
            <p className="text-[11.5px] leading-snug text-muted-foreground">{field.hint}</p>
          ) : null}
        </div>
      ))}
    </>
  );
}

function authParamLabel(name: string): string {
  return name
    .split('_')
    .filter(Boolean)
    .map((part) => part[0]?.toUpperCase() + part.slice(1))
    .join(' ');
}

function BasicAuthUsernameField({
  value,
  error,
  onChange,
}: {
  value: string;
  error?: string;
  onChange: (value: string) => void;
}): React.JSX.Element {
  return (
    <div className="space-y-1.5">
      <Label htmlFor="reg-auth-username">Basic auth username</Label>
      <Input
        id="reg-auth-username"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="e.g. integration@example.com"
        autoComplete="off"
      />
      {error && <p className="text-[11.5px] text-destructive">{error}</p>}
    </div>
  );
}
